import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Toolbar,
  AppBar,
  Snackbar,
  Switch,
  LinearProgress,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MenuIcon from '@mui/icons-material/Menu';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import api from '../services/api';
import { PageName, TaskType, ApiResponse, User, TaskSummary, TaskDetails, AssignedUser } from '../types';
import { DrawerNavigation, ConfirmDialog } from '../components';
import { DRAWER_WIDTH } from '../constants/ui';
import { TASK_TYPE_CONFIG } from '../constants/taskConfig';

interface TaskManagementProps {
  onLogout: () => void;
  onNavigate: (page: PageName, taskId?: string) => void;
}

const TaskManagement: React.FC<TaskManagementProps> = ({ onLogout, onNavigate }) => {
  // Data state
  const [tasks, setTasks] = useState<TaskSummary[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasFetched = useRef(false);

  // Filter state
  const [titleFilter, setTitleFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState<TaskType | ''>('');
  const [userFilter, setUserFilter] = useState('');

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskSummary | null>(null);
  const [editingTaskDetails, setEditingTaskDetails] = useState<TaskDetails | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editAssignedTo, setEditAssignedTo] = useState<string[]>([]);
  const [editFinished, setEditFinished] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Clear confirmation dialog state
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [clearField, setClearField] = useState<'completed' | 'noAction' | 'samples' | null>(null);
  const [clearing, setClearing] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Drawer state
  const [drawerCollapsed, setDrawerCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Delete task state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<TaskSummary | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Fetch tasks and users on mount
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        const [tasksRes, usersRes] = await Promise.all([
          api.get<ApiResponse<TaskSummary[]>>('tasks/list'),
          api.get<ApiResponse<User[]>>('users/list'),
        ]);

        setTasks(tasksRes.data.results || []);
        setUsers(usersRes.data.results || []);
      } catch (err) {
        console.error('Failed to fetch data:', err);
        setError('Failed to load tasks');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Refresh tasks list
  const refreshTasks = async () => {
    try {
      setLoading(true);
      const response = await api.get<ApiResponse<TaskSummary[]>>('tasks/list');
      setTasks(response.data.results || []);
    } catch (err) {
      console.error('Failed to refresh tasks:', err);
      setError('Failed to refresh tasks');
    } finally {
      setLoading(false);
    }
  };

  // Filtered and sorted tasks (newest first by updatedAt)
  const filteredTasks = useMemo(() => {
    return tasks
      .filter((task) => {
        // Title filter (case-insensitive contains)
        if (titleFilter && !task.title.toLowerCase().includes(titleFilter.toLowerCase())) {
          return false;
        }
        // Type filter
        if (typeFilter && task.type !== typeFilter) {
          return false;
        }
        // User filter (any assigned user matches)
        if (userFilter && !task.assignedTo?.some((u) => u.id === userFilter)) {
          return false;
        }
        return true;
      })
      .sort((a, b) => b.updatedAt - a.updatedAt);
  }, [tasks, titleFilter, typeFilter, userFilter]);

  // Sorted users for dropdown
  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      const nameA = a.firstName && a.lastName ? `${a.firstName} ${a.lastName}` : a.username;
      const nameB = b.firstName && b.lastName ? `${b.firstName} ${b.lastName}` : b.username;
      return nameA.localeCompare(nameB);
    });
  }, [users]);

  // Open edit dialog
  const handleEditClick = async (task: TaskSummary) => {
    setEditingTask(task);
    setEditTitle(task.title);
    setEditDescription(task.description || '');
    setEditAssignedTo(task.assignedTo?.map((u) => u.id) || []);
    setEditFinished(task.finished ?? false);
    setEditDialogOpen(true);
    setLoadingDetails(true);

    try {
      const response = await api.get<ApiResponse<TaskDetails>>(`tasks/details/${task.id}`);
      const details = response.data.results;
      setEditingTaskDetails(details);
      setEditDescription(details.description || '');
      setEditAssignedTo(details.assignedTo?.map((u: any) => u.id) || []);
      setEditFinished(details.finished ?? false);
    } catch (err) {
      console.error('Failed to fetch task details:', err);
      setSnackbar({ open: true, message: 'Failed to load task details', severity: 'error' });
    } finally {
      setLoadingDetails(false);
    }
  };

  // Close edit dialog
  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setEditingTask(null);
    setEditingTaskDetails(null);
    setEditTitle('');
    setEditDescription('');
    setEditAssignedTo([]);
    setEditFinished(false);
  };

  // Save task changes
  const handleSaveTask = async () => {
    if (!editingTask) return;

    setSaving(true);
    try {
      await api.patch(`tasks/patch/${editingTask.id}`, {
        title: editTitle,
        description: editDescription,
        assignedTo: editAssignedTo.map((id) => ({ id })),
        finished: editFinished,
      });

      // Update local state
      setTasks((prev) =>
        prev.map((t) =>
          t.id === editingTask.id
            ? {
                ...t,
                title: editTitle,
                description: editDescription,
                assignedTo: editAssignedTo
                  .map((id) => users.find((u) => u.id === id))
                  .filter(Boolean) as AssignedUser[],
                finished: editFinished,
              }
            : t
        )
      );

      setSnackbar({ open: true, message: 'Task updated successfully', severity: 'success' });
      handleEditDialogClose();
    } catch (err) {
      console.error('Failed to save task:', err);
      setSnackbar({ open: true, message: 'Failed to save task', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Open clear confirmation dialog
  const handleClearClick = (field: 'completed' | 'noAction' | 'samples') => {
    setClearField(field);
    setClearConfirmOpen(true);
  };

  // Confirm clear field
  const handleClearConfirm = async () => {
    if (!editingTaskDetails || !clearField) return;

    const currentCount =
      clearField === 'samples'
        ? editingTaskDetails.samplesCount
        : clearField === 'completed'
        ? editingTaskDetails.completedCount
        : editingTaskDetails.noActionCount;

    if (currentCount === 0) {
      setSnackbar({ open: true, message: `No ${clearField} to clear`, severity: 'info' });
      setClearConfirmOpen(false);
      setClearField(null);
      return;
    }

    setClearing(true);
    try {
      // Use PATCH with empty array to clear the field
      // When clearing samples, also clear completed and noAction, and set finished to false
      if (clearField === 'samples') {
        await api.patch(`tasks/patch/${editingTaskDetails.id}`, {
          samples: [],
          completed: [],
          noAction: [],
          finished: false,
        });
      } else {
        await api.patch(`tasks/patch/${editingTaskDetails.id}`, {
          [clearField]: [],
        });
      }

      // Update local task details state
      setEditingTaskDetails((prev) => {
        if (!prev) return null;
        if (clearField === 'samples') {
          return {
            ...prev,
            samples: [],
            samplesCount: 0,
            completed: [],
            completedCount: 0,
            noAction: [],
            noActionCount: 0,
            finished: false,
          };
        }
        return {
          ...prev,
          [clearField]: [],
          [`${clearField}Count`]: 0,
        };
      });

      // Update task counts in main list
      setTasks((prev) =>
        prev.map((t) => {
          if (t.id !== editingTaskDetails.id) return t;
          if (clearField === 'samples') {
            return { ...t, samplesCount: 0, completedCount: 0, noActionCount: 0, finished: false };
          }
          return { ...t, [`${clearField}Count`]: 0 };
        })
      );

      setSnackbar({
        open: true,
        message: `Cleared ${currentCount} items from ${clearField}`,
        severity: 'success',
      });
    } catch (err) {
      console.error(`Failed to clear ${clearField}:`, err);
      setSnackbar({ open: true, message: `Failed to clear ${clearField}`, severity: 'error' });
    } finally {
      setClearing(false);
      setClearConfirmOpen(false);
      setClearField(null);
    }
  };

  // Copy task ID to clipboard
  const handleCopyId = async (id: string) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(id);
      } else {
        // Fallback for non-HTTPS contexts
        const textArea = document.createElement('textarea');
        textArea.value = id;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setSnackbar({ open: true, message: 'Task ID copied', severity: 'success' });
    } catch (err) {
      console.error('Failed to copy:', err);
      setSnackbar({ open: true, message: 'Failed to copy ID', severity: 'error' });
    }
  };

  // Delete task handlers
  const handleDeleteClick = (task: TaskSummary) => {
    setTaskToDelete(task);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!taskToDelete) return;
    setDeleting(true);
    try {
      await api.delete(`tasks/delete/${taskToDelete.id}`);
      setTasks((prev) => prev.filter((t) => t.id !== taskToDelete.id));
      setSnackbar({ open: true, message: 'Task deleted', severity: 'success' });
    } catch (err) {
      console.error('Failed to delete task:', err);
      setSnackbar({ open: true, message: 'Failed to delete task', severity: 'error' });
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setTaskToDelete(null);
    }
  };

  // Quick clear samples from table row
  const handleQuickClearSamples = (task: TaskSummary) => {
    setEditingTask(task);
    // Create minimal task details for the clear confirmation to work
    setEditingTaskDetails({
      id: task.id,
      title: task.title,
      type: task.type,
      description: task.description,
      assignedTo: task.assignedTo || [],
      samples: [],
      samplesCount: task.samplesCount,
      completed: [],
      completedCount: task.completedCount,
      noAction: [],
      noActionCount: task.noActionCount,
      createdAt: 0,
      updatedAt: 0,
    } as TaskDetails);
    setClearField('samples');
    setClearConfirmOpen(true);
  };

  // Get user display name
  const getUserDisplayName = (user: AssignedUser | User): string => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.username;
  };

  // Get clear field label
  const getClearFieldLabel = (field: 'completed' | 'noAction' | 'samples'): string => {
    switch (field) {
      case 'completed':
        return 'Completed';
      case 'noAction':
        return 'No Action';
      case 'samples':
        return 'All Samples';
    }
  };

  // Get current count for clear field
  const getClearFieldCount = (): number => {
    if (!editingTaskDetails || !clearField) return 0;
    switch (clearField) {
      case 'samples':
        return editingTaskDetails.samplesCount;
      case 'completed':
        return editingTaskDetails.completedCount;
      case 'noAction':
        return editingTaskDetails.noActionCount;
    }
  };

  const currentDrawerWidth = drawerCollapsed ? 56 : DRAWER_WIDTH;

  return (
    <Box sx={{ display: 'flex' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: `${currentDrawerWidth}px` },
          transition: 'width 0.2s, margin 0.2s',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setMobileOpen(true)}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Task Management
          </Typography>
          <Button
            color="inherit"
            onClick={onLogout}
            startIcon={<LogoutIcon />}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <DrawerNavigation
        currentPage="taskManagement"
        onNavigate={onNavigate}
        collapsed={drawerCollapsed}
        onToggleCollapse={() => setDrawerCollapsed(!drawerCollapsed)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: `${currentDrawerWidth}px` },
          transition: 'width 0.2s, margin 0.2s',
        }}
      >
        <Toolbar />
        <Container maxWidth="xl">
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Create Task Button */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AddIcon />}
              onClick={() => onNavigate('taskCreate')}
            >
              New Task
            </Button>
          </Box>

          {/* Filters */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Filters
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Title"
                value={titleFilter}
                onChange={(e) => setTitleFilter(e.target.value)}
                size="small"
                sx={{ minWidth: 200 }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value as TaskType | '')}
                  label="Type"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="mmc">MMC</MenuItem>
                  <MenuItem value="detection">Detection</MenuItem>
                  <MenuItem value="plate">Plate</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Assigned To</InputLabel>
                <Select
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  label="Assigned To"
                >
                  <MenuItem value="">All Users</MenuItem>
                  {sortedUsers.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {getUserDisplayName(user)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={refreshTasks}
                disabled={loading}
              >
                Refresh
              </Button>
            </Box>
          </Paper>

          {/* Tasks Table */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Assigned To</TableCell>
                    <TableCell>Samples</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell>Updated</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTasks.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography color="text.secondary">No tasks found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTasks.map((task) => {
                      const config = TASK_TYPE_CONFIG[task.type];
                      const Icon = config.icon;
                      return (
                        <TableRow key={task.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: 'monospace',
                                  fontSize: '0.75rem',
                                  color: 'text.secondary',
                                }}
                              >
                                {task.id.slice(0, 8)}...
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => handleCopyId(task.id)}
                                title="Copy task ID"
                                sx={{ p: 0.25 }}
                              >
                                <ContentCopyIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography fontWeight={500}>{task.title}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={<Icon size={16} />}
                              label={config.shortLabel}
                              size="small"
                              sx={{
                                bgcolor: config.color,
                                color: '#fff',
                                '& .MuiChip-icon': { color: '#fff' },
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                maxWidth: 250,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {task.description || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {task.assignedTo && task.assignedTo.length > 0 ? (
                              <Typography variant="body2">
                                {task.assignedTo.map((u) => getUserDisplayName(u)).join(', ')}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Unassigned
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{task.samplesCount}</Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: 120 }}>
                            {task.samplesCount > 0 ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.round(((task.completedCount + task.noActionCount) / task.samplesCount) * 100)}
                                  sx={{
                                    flexGrow: 1,
                                    height: 8,
                                    borderRadius: 4,
                                    bgcolor: 'action.disabledBackground',
                                    '& .MuiLinearProgress-bar': {
                                      borderRadius: 4,
                                    },
                                  }}
                                />
                                <Typography variant="caption" sx={{ minWidth: 35 }}>
                                  {Math.round(((task.completedCount + task.noActionCount) / task.samplesCount) * 100)}%
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {task.updatedAt ? new Date(task.updatedAt * 1000).toLocaleString() : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {task.finished ? (
                              <CheckCircleOutlineIcon color="success" titleAccess="Finished" />
                            ) : (
                              <RadioButtonUncheckedIcon color="disabled" titleAccess="Active" />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => onNavigate('taskPage', task.id)}
                              title="Open task"
                              color="primary"
                            >
                              <OpenInNewIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleEditClick(task)}
                              title="Edit task"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleQuickClearSamples(task)}
                              title="Clear all samples"
                              color="warning"
                              disabled={task.samplesCount === 0}
                            >
                              <ClearAllIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(task)}
                              title="Delete task"
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Container>
      </Box>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Edit Task</Typography>
          <IconButton onClick={handleEditDialogClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loadingDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {/* Title */}
              <TextField
                label="Title"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                fullWidth
              />

              {/* Description */}
              <TextField
                label="Description"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                fullWidth
                multiline
                rows={3}
              />

              {/* Type (read-only) */}
              {editingTask && (() => {
                const TypeIcon = TASK_TYPE_CONFIG[editingTask.type].icon;
                return (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Type (immutable)
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        icon={<TypeIcon size={16} />}
                        label={TASK_TYPE_CONFIG[editingTask.type].label}
                        sx={{
                          bgcolor: TASK_TYPE_CONFIG[editingTask.type].color,
                          color: '#fff',
                          '& .MuiChip-icon': { color: '#fff' },
                        }}
                      />
                    </Box>
                  </Box>
                );
              })()}

              {/* Assigned To */}
              <FormControl fullWidth>
                <InputLabel>Assigned To</InputLabel>
                <Select
                  multiple
                  value={editAssignedTo}
                  onChange={(e) => setEditAssignedTo(e.target.value as string[])}
                  input={<OutlinedInput label="Assigned To" />}
                  renderValue={(selected) =>
                    selected
                      .map((id) => {
                        const user = users.find((u) => u.id === id);
                        return user ? getUserDisplayName(user) : id;
                      })
                      .join(', ')
                  }
                >
                  {sortedUsers.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      <Checkbox checked={editAssignedTo.includes(user.id)} />
                      <ListItemText primary={getUserDisplayName(user)} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Finished Toggle */}
              <FormControlLabel
                control={
                  <Switch
                    checked={editFinished}
                    onChange={(e) => setEditFinished(e.target.checked)}
                    color="success"
                  />
                }
                label="Mark as Finished"
              />

              {/* Progress Section */}
              {editingTaskDetails && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Progress
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">
                        Samples: <strong>{editingTaskDetails.samplesCount}</strong>
                      </Typography>
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        onClick={() => handleClearClick('samples')}
                        disabled={editingTaskDetails.samplesCount === 0}
                        startIcon={<DeleteIcon />}
                      >
                        Clear All
                      </Button>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">
                        Completed:{' '}
                        <strong>
                          {editingTaskDetails.completedCount} (
                          {editingTaskDetails.samplesCount > 0
                            ? Math.round((editingTaskDetails.completedCount / editingTaskDetails.samplesCount) * 100)
                            : 0}
                          %)
                        </strong>
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleClearClick('completed')}
                        disabled={editingTaskDetails.completedCount === 0}
                        startIcon={<DeleteIcon />}
                      >
                        Clear
                      </Button>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">
                        No Action:{' '}
                        <strong>
                          {editingTaskDetails.noActionCount} (
                          {editingTaskDetails.samplesCount > 0
                            ? Math.round((editingTaskDetails.noActionCount / editingTaskDetails.samplesCount) * 100)
                            : 0}
                          %)
                        </strong>
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleClearClick('noAction')}
                        disabled={editingTaskDetails.noActionCount === 0}
                        startIcon={<DeleteIcon />}
                      >
                        Clear
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditDialogClose}>Cancel</Button>
          <Button
            onClick={handleSaveTask}
            variant="contained"
            disabled={saving || loadingDetails}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear Confirmation Dialog */}
      <Dialog open={clearConfirmOpen} onClose={() => setClearConfirmOpen(false)}>
        <DialogTitle>
          {clearField === 'samples' ? 'Clear All Samples?' : `Clear ${getClearFieldLabel(clearField || 'completed')}?`}
        </DialogTitle>
        <DialogContent>
          {clearField === 'samples' ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This will remove ALL {getClearFieldCount()} samples from this task. This action cannot be undone.
            </Alert>
          ) : (
            <Typography>
              Are you sure you want to clear {getClearFieldCount()} items from{' '}
              {getClearFieldLabel(clearField || 'completed')}?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearConfirmOpen(false)} disabled={clearing}>
            Cancel
          </Button>
          <Button
            onClick={handleClearConfirm}
            color={clearField === 'samples' ? 'error' : 'primary'}
            variant="contained"
            disabled={clearing}
          >
            {clearing ? 'Clearing...' : 'Clear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Task Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setTaskToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={`Delete Task "${taskToDelete?.title}"?`}
        message="This will permanently delete this task. This action cannot be undone."
        loading={deleting}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TaskManagement;
